import axios, { AxiosError, AxiosResponse } from 'axios';
import type { TokenResponse, User } from './types/api';

// Default 30s — long enough for Fly cold starts, short enough that a stuck
// upstream times out instead of leaving the UI spinning forever. Long-running
// endpoints (AI calls, document export) get bumped to 120s by the request
// interceptor below.
const DEFAULT_TIMEOUT_MS = 30_000;
const LONG_TIMEOUT_MS = 120_000;
const LONG_ENDPOINT_PATTERNS = [
  /\/generate-docx$/,
  /\/export\/pdf$/,
  /\/export\/pptx$/,
  /\/api\/ai\//,
  /\/api\/proposals\/[^/]+\/draft/,
  /\/api\/rfp\/parse/,
];

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000',
  headers: { 'Content-Type': 'application/json' },
  timeout: DEFAULT_TIMEOUT_MS,
  withCredentials: true,
});

/**
 * In-memory access token used as a Safari fallback. Set by AuthContext on
 * login/register/refresh. Without this, Safari/WebKit users hit 401 on
 * every navigation: ITP (Intelligent Tracking Prevention) treats the
 * cross-subdomain api.fly.dev ↔ app.fly.dev cookie as third-party and
 * silently drops it, even with SameSite=None+Secure. The Authorization
 * header path bypasses the cookie problem entirely.
 *
 * Mirror to localStorage so a page refresh in Safari can restore the
 * session — without it, every refresh on Safari = back to login.
 */
let inMemoryToken: string | null = (() => {
  try {
    return localStorage.getItem('bidforge-access-token');
  } catch {
    return null;
  }
})();

export function setAuthToken(token: string | null): void {
  inMemoryToken = token;
  try {
    if (token) localStorage.setItem('bidforge-access-token', token);
    else localStorage.removeItem('bidforge-access-token');
  } catch {
    // Storage disabled (private browsing) — fall back to memory only.
  }
}

export function getAuthToken(): string | null {
  return inMemoryToken;
}

// Attach Authorization header (Safari fallback) + X-Organization-Id +
// extend timeout for long-running endpoints.
api.interceptors.request.use((config) => {
  if (inMemoryToken && !config.headers['Authorization']) {
    config.headers['Authorization'] = `Bearer ${inMemoryToken}`;
  }
  const orgId = localStorage.getItem('bidforge-org-id');
  if (orgId) {
    config.headers['X-Organization-Id'] = orgId;
  }
  if (config.url && LONG_ENDPOINT_PATTERNS.some((re) => re.test(config.url!))) {
    config.timeout = LONG_TIMEOUT_MS;
  }
  return config;
});

/**
 * Extract a human-readable error message from any axios/fetch failure.
 * Replaces the 30+ ad-hoc `err.response?.data?.detail || 'Failed to X'`
 * inline patterns scattered across pages — use this everywhere instead.
 */
export function extractApiError(err: unknown): string {
  const ax = err as AxiosError<{ detail?: string; error?: { message?: string } }>;
  if (ax?.code === 'ECONNABORTED') return 'Request timed out — please try again.';
  if (ax?.code === 'ERR_NETWORK') return 'Network error — check your connection.';
  return (
    ax?.response?.data?.detail
    ?? ax?.response?.data?.error?.message
    ?? ax?.message
    ?? 'Request failed'
  );
}

let isRefreshing = false;
let failedQueue: Array<{ resolve: (v: any) => void; reject: (e: any) => void }> = [];

const processQueue = (error: any = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(undefined);
  });
  failedQueue = [];
};

// Cross-page refresh debounce: a successful refresh is good for ~15 minutes;
// don't fire another one until then even if multiple tabs/navigations 401.
// Without this, fast navigation across pages can trigger a refresh storm
// that hits the rate limiter (then EVERY subsequent refresh 429s, kicking
// users out silently).
let lastRefreshAt = 0;
const REFRESH_DEBOUNCE_MS = 5 * 60 * 1000;  // 5 min

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip interceptor for auth endpoints — they handle their own 401s
    const isAuthEndpoint = originalRequest.url?.includes('/api/auth/');
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint &&
      window.location.pathname !== '/login'
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }

      // If we just refreshed and STILL got a 401, the refresh token itself
      // is invalid — no point trying again, just propagate so AuthContext
      // can show the login page.
      if (Date.now() - lastRefreshAt < REFRESH_DEBOUNCE_MS) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await api.post('/api/auth/refresh', {}, { withCredentials: true });
        lastRefreshAt = Date.now();
        processQueue();
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        // Don't redirect — let the AuthContext handle unauthenticated state
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ── Auth ────────────────────────────────────────────────────

export const auth = {
  login: (data: { email: string; password: string }): Promise<AxiosResponse<TokenResponse>> =>
    api.post('/api/auth/login', data),
  register: (data: { name: string; email: string; password: string }): Promise<AxiosResponse<TokenResponse>> =>
    api.post('/api/auth/register', data),
  refresh: (): Promise<AxiosResponse<TokenResponse>> =>
    api.post('/api/auth/refresh'),
  logout: (): Promise<AxiosResponse<{ detail: string }>> =>
    api.post('/api/auth/logout'),
  me: (): Promise<AxiosResponse<User>> =>
    api.get('/api/auth/me'),
  updateProfile: (data: { name?: string; email?: string }) =>
    api.put('/api/auth/profile', data),
  changePassword: (data: { current_password: string; new_password: string }) =>
    api.post('/api/auth/change-password', data),
  forgotPassword: (data: { email: string }) =>
    api.post('/api/auth/forgot-password', data),
  resetPassword: (data: { token: string; new_password: string }) =>
    api.post('/api/auth/reset-password', data),
  acceptInvite: (data: { token: string; name: string; password: string }) =>
    api.post('/api/auth/accept-invite', data),
};

// ── Onboarding ──────────────────────────────────────────────

export const onboarding = {
  getStatus: () => api.get('/api/onboarding/status'),
  updateStep: (step: number, data: Record<string, unknown>) => api.put(`/api/onboarding/step/${step}`, data),
  uploadLogo: (formData: FormData) =>
    api.post('/api/onboarding/step/1/upload-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  uploadPeopleCSV: (formData: FormData) =>
    api.post('/api/onboarding/step/2/import-team', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  uploadProjectsCSV: (formData: FormData) =>
    api.post('/api/onboarding/step/3/import-projects', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  skipStep: (step: number) => api.post(`/api/onboarding/skip/${step}`),
  complete: () => api.post('/api/onboarding/complete'),
};

// ── Projects ────────────────────────────────────────────────

export const projects = {
  list: (params?: Record<string, unknown>) => api.get('/api/projects', { params }),
  get: (id: string) => api.get(`/api/projects/${id}`),
  create: (data: Record<string, unknown>) => api.post('/api/projects', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/api/projects/${id}`, data),
  delete: (id: string) => api.delete(`/api/projects/${id}`),
  listImages: (id: string) => api.get(`/api/projects/${id}/images`),
  uploadImage: (id: string, formData: FormData) =>
    api.post(`/api/projects/${id}/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteImage: (projectId: string, imageId: string) =>
    api.delete(`/api/projects/${projectId}/images/${imageId}`),
  // Returns { project_id, team[], proposal_count }. Team is derived from
  // proposals that included this project — see backend route docstring.
  getTeam: (id: string) => api.get(`/api/projects/${id}/team`),
};

// ── People ──────────────────────────────────────────────────

export const people = {
  list: (params?: Record<string, unknown>) => api.get('/api/people', { params }),
  get: (id: string) => api.get(`/api/people/${id}`),
  create: (data: Record<string, unknown>) => api.post('/api/people', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/api/people/${id}`, data),
  delete: (id: string) => api.delete(`/api/people/${id}`),
  uploadCV: (id: string, formData: FormData) =>
    api.post(`/api/people/${id}/upload-cv`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  uploadPhoto: (id: string, formData: FormData) =>
    api.post(`/api/people/${id}/upload-photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// ── Templates ───────────────────────────────────────────────

export const templates = {
  list: (params?: Record<string, unknown>) => api.get('/api/templates', { params }),
  get: (id: string) => api.get(`/api/templates/${id}`),
  create: (data: Record<string, unknown>) => api.post('/api/templates', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/api/templates/${id}`, data),
  delete: (id: string) => api.delete(`/api/templates/${id}`),
  clone: (id: string) => api.post(`/api/templates/${id}/clone`),
  suggest: (disciplines: string[]) => api.post('/api/templates/suggest', { disciplines }),
};

// ── Proposals ───────────────────────────────────────────────

// ── Grounding source-trace (faithfulness) ───────────────────
// Per-claim attribution emitted by POST /api/ai/verify-faithfulness.
// Each claim is checked against the proposal's real org records; the
// backend resolver drops any citation that doesn't resolve to a real
// source, so `sources` here are always genuine.
export interface GroundingSourceRef { type: 'project' | 'person' | 'org' | 'rfp'; id: string; label: string; }
export interface GroundingClaim { text: string; status: 'supported' | 'unsupported' | 'unverifiable'; sources: GroundingSourceRef[]; }
export interface FaithfulnessResult {
  score: number; verdict: 'grounded' | 'review' | 'unsupported';
  unsupported_claims: string[]; backend: string; claims: GroundingClaim[];
}

export const proposals = {
  list: (params?: Record<string, unknown>) => api.get('/api/proposals', { params }),
  get: (id: string) => api.get(`/api/proposals/${id}`),
  create: (data: Record<string, unknown>) => api.post('/api/proposals', data),
  createFromRfp: (data: Record<string, unknown>) => api.post('/api/proposals/from-rfp', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/api/proposals/${id}`, data),
  delete: (id: string) => api.delete(`/api/proposals/${id}`),
  addProject: (proposalId: string, projectId: string, data?: Record<string, unknown>) =>
    api.post(`/api/proposals/${proposalId}/projects`, { project_id: projectId, ...data }),
  removeProject: (proposalId: string, projectId: string) =>
    api.delete(`/api/proposals/${proposalId}/projects/${projectId}`),
  addPerson: (proposalId: string, personId: string, data?: Record<string, unknown>) =>
    api.post(`/api/proposals/${proposalId}/people`, { person_id: personId, ...data }),
  // F104 (Resource Planner wiring 2026-05-18): patch an existing
  // assignment to change role / hours_allocated. Used by the edit-hours
  // chip affordance in ProposalBuilder's right rail. Without this the
  // user had no way to revise hours after the initial add — full
  // remove-then-re-add was the only workaround.
  updatePerson: (proposalId: string, personId: string, data: { role_on_proposal?: string; hours_allocated?: number | null; sort_order?: number }) =>
    api.patch(`/api/proposals/${proposalId}/people/${personId}`, data),
  removePerson: (proposalId: string, personId: string) =>
    api.delete(`/api/proposals/${proposalId}/people/${personId}`),
  generateDocx: (id: string) =>
    api.post(`/api/proposals/${id}/generate-docx`, null, {
      responseType: 'blob',
    }),
  // Grounding source-trace — re-runs the Claude faithfulness judge for a
  // proposal (optionally a single section) and returns per-claim
  // attribution. Persists the trace on proposal.content server-side.
  verifyFaithfulness: (proposalId: string, sectionName?: string) =>
    api.post<{ enabled: boolean; result: FaithfulnessResult | null; message?: string }>(
      '/api/ai/verify-faithfulness',
      { proposal_id: proposalId, section_name: sectionName }),
  // F199.1 — optional rationale captured on win/loss transitions.
  // Callers can omit it for routine transitions (draft→in_progress);
  // pass it for status='won'|'lost' so the Decision Log row carries
  // the "why" alongside the outcome.
  updateStatus: (id: string, status: string, rationale?: string) =>
    api.patch(`/api/proposals/${id}/status`, rationale ? { status, rationale } : { status }),
  clone: (id: string) => api.post(`/api/proposals/${id}/clone`),
  activity: (id: string) => api.get(`/api/proposals/${id}/activity`),
  // T3 (F410+F411) — estimate-vs-actual hours rollup from
  // ProposalPerson.hours_allocated + TimeEntry SUM. Used by
  // ActualsWidget in ProposalBuilder right rail.
  actuals: (id: string) => api.get(`/api/proposals/${id}/actuals`),
  // T9 (2026-05-23) — burn-rate-based cost-to-complete forecast for
  // active proposals with logged time. Used by CostToCompleteWidget
  // in ProposalBuilder right rail (self-hides when has_data=false).
  costToComplete: (id: string) => api.get(`/api/proposals/${id}/cost-to-complete`),
  // F340 — cross-source contradiction detector. Returns
  // {conflicts: [...]}. Empty when <2 source docs or none found.
  sourceConflicts: (id: string) => api.get(`/api/proposals/${id}/source-conflicts`),
  // F332 — Excel calc appendix upload. Returns extraction summary;
  // the actual markdown is stored on proposal.content.calculation_appendix
  // for the DOCX/PDF export pipeline to render.
  uploadCalcAppendix: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/api/proposals/${id}/calc-appendix`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // F330 — upload an IFC BIM model; ~50MB cap server-side. Server
  // extracts storeys + area + structural system + materials and
  // stores summary on proposal.content for AI/export use.
  uploadBim: (id: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/api/proposals/${id}/bim-attach`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getEngagement: (id: string) => api.get(`/api/proposals/${id}/engagement`),
  exportPdf: (id: string) => api.get(`/api/proposals/${id}/export/pdf`, { responseType: 'blob' }),
  exportPptx: (id: string) => api.get(`/api/proposals/${id}/export/pptx`, { responseType: 'blob' }),
  publishWeb: (id: string) => api.post(`/api/proposals/${id}/publish-web`),
  unpublishWeb: (id: string) => api.delete(`/api/proposals/${id}/publish-web`),
  getViews: (id: string) => api.get(`/api/proposals/${id}/views`),
  getPublicProposal: (token: string) => api.get(`/api/public/proposals/${token}`),
  acceptPublic: (token: string) => api.post(`/api/public/proposals/${token}/accept`),
  declinePublic: (token: string) => api.post(`/api/public/proposals/${token}/decline`),
  trackEngagement: (token: string, data: Record<string, unknown>) => api.post(`/api/public/proposals/${token}/track`, data),
  listMedia: (id: string) => api.get(`/api/proposals/${id}/media`),
  uploadMedia: (id: string, formData: FormData) =>
    api.post(`/api/proposals/${id}/media`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteMedia: (proposalId: string, mediaId: string) =>
    api.delete(`/api/proposals/${proposalId}/media/${mediaId}`),
  exportReadiness: (id: string) => api.get(`/api/proposals/${id}/export-readiness`),
  readiness: (id: string) => api.get(`/api/proposals/${id}/readiness`),
  pilotMetrics: () => api.get('/api/proposals/pilot-metrics'),
  submissionReadiness: (id: string) => api.get(`/api/proposals/${id}/submission-readiness`),
  submissionPackage: (id: string) =>
    api.get(`/api/proposals/${id}/submission-package`, { responseType: 'blob' }),
  uploadCustomTemplate: (id: string, formData: FormData) =>
    api.post(`/api/proposals/${id}/custom-template`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  clearCustomTemplate: (id: string) =>
    api.delete(`/api/proposals/${id}/custom-template`),
  getRfpDocument: (id: string) => api.get(`/api/proposals/${id}/rfp-document`),
  getRfpText: (id: string) => api.get(`/api/proposals/${id}/rfp-document/text`),
  downloadRfpDocument: (id: string) =>
    api.get(`/api/proposals/${id}/rfp-document/download`, { responseType: 'blob' }),
};

// ── Fee Estimation ──────────────────────────────────────────

export const fee = {
  getRateCards: () => api.get('/api/fee/rate-cards'),
  createRateCard: (data: Record<string, unknown>) => api.post('/api/fee/rate-cards', data),
  updateRateCard: (id: string, data: Record<string, unknown>) => api.put(`/api/fee/rate-cards/${id}`, data),
  deleteRateCard: (id: string) => api.delete(`/api/fee/rate-cards/${id}`),
  calculateEstimate: (data: Record<string, unknown>) => api.post('/api/fee/estimate/validate', data),
};

// ── Analytics ───────────────────────────────────────────────

export const analytics = {
  overview: () => api.get('/api/analytics/overview'),
  winLoss: () => api.get('/api/analytics/win-loss'),
  pipeline: () => api.get('/api/analytics/pipeline'),
};

// ── Dashboard widgets ───────────────────────────────────────

export const dashboard = {
  directorConfidence: (limit = 5) =>
    api.get(`/api/dashboard/director-confidence?limit=${limit}`),
};

// ── AI ─────────────────────────────────────────────────────

export const ai = {
  draftSection: (data: Record<string, unknown>) => api.post('/api/ai/draft-section', data),
  // F215 — AI-suggested win/loss rationale for human review at capture
  // time. Frontend calls this when user clicks "Suggest" in the
  // win/loss rationale prompt; user edits/accepts the returned text.
  suggestRationale: (data: { proposal_id: string; outcome: 'won' | 'lost' }) =>
    api.post('/api/ai/suggest-rationale', data),
  executiveSummary: (data: Record<string, unknown>) => api.post('/api/ai/executive-summary', data),
  rewrite: (data: Record<string, unknown>) => api.post('/api/ai/rewrite', data),
  qualityScore: (data: { text: string; section_name: string; proposal_id?: string; evaluation_criteria?: string }) =>
    api.post('/api/ai/quality-score', data),
  reviewProposal: (proposalId: string) =>
    api.post('/api/ai/review-proposal', { proposal_id: proposalId }),
  voiceToProposal: (data: { transcription: string; section_name?: string; proposal_context?: string }) =>
    api.post('/api/ai/voice-to-proposal', data),
  usage: () => api.get('/api/ai/usage'),
  usageSummary: () => api.get('/api/ai/usage-summary'),
  throttleStatus: () => api.get('/api/ai/throttle-status'),
};

// ── RFP Parser ─────────────────────────────────────────────

export const rfp = {
  parse: (file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/api/rfp/parse', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // F160 (Phase 26, 2026-05-19): batch upload up to 20 files in a single
  // request. Returns {documents: [...], primary_document_id, ...}.
  // Use when a user drags a real tender pack (main RFP + appendices +
  // Q&A addenda etc) onto the upload zone.
  parseBatch: (files: File[]) => {
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    return api.post('/api/rfp/parse-batch', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// ── Go/No-Go ───────────────────────────────────────────────

export const goNoGo = {
  evaluate: (criteria: Record<string, unknown>) => api.post('/api/go-nogo/evaluate', criteria),
  // F182 — persist a Go/No-Go evaluation to the Decision Log with
  // optional rationale text. Spec: docs/superpowers/specs/2026-05-22-decision-log-design.md
  persist: (payload: {
    proposal_id?: string;
    criteria: Record<string, unknown>;
    verdict: string;
    total_score?: number;
    rationale?: string;
  }) => api.post('/api/go-nogo/persist', payload),
};

// ── NZ Government Wizard ─────────────────────────────────

export const nzGovt = {
  readiness: () => api.get('/api/nz-govt/readiness'),
};

// ── Win Probability ────────────────────────────────────────

export const winProbability = (proposalId: string) =>
  api.post(`/api/proposals/${proposalId}/win-probability`);

// ── Compliance Check ───────────────────────────────────────

export const complianceCheck = (proposalId: string) =>
  api.post(`/api/proposals/${proposalId}/compliance-check`);

// ── Smart Team ─────────────────────────────────────────────

export const smartTeam = {
  recommend: (requirements: Record<string, unknown>) => api.post('/api/team/recommend', requirements),
};

// ── Content Health ─────────────────────────────────────────

export const contentHealth = {
  check: () => api.get('/api/content-health'),
};

// ── Gamification ───────────────────────────────────────────

export const gamification = {
  stats: () => api.get('/api/gamification/stats'),
};

// ── SME Micro-Form ─────────────────────────────────────────

export const sme = {
  getForm: (id: string) => api.get(`/api/sme/micro-form/${id}`),
  respond: (id: string, data: Record<string, unknown>) => api.post(`/api/sme/micro-form/${id}/respond`, data),
  create: (data: Record<string, unknown>) => api.post('/api/sme/micro-form', data),
};

// ── Compliance Matrix (F4) ─────────────────────────────────

export const complianceMatrix = {
  // Legacy method names — kept for callers built before the shred/gap-analysis additions.
  list: (proposalId: string) => api.get(`/api/proposals/${proposalId}/compliance-matrix`),
  update: (proposalId: string, itemId: string, data: Record<string, unknown>) =>
    api.patch(`/api/proposals/${proposalId}/compliance-matrix/${itemId}`, data),
  // Newer methods — pasted-RFP shred + gap analysis added alongside the
  // ComplianceMatrix workspace page.
  get: (proposalId: string) => api.get(`/api/proposals/${proposalId}/compliance-matrix`),
  generate: (proposalId: string) => api.post(`/api/proposals/${proposalId}/compliance-matrix/generate`),
  shredPreview: (proposalId: string, rawText: string, useAi = false) =>
    api.post(`/api/proposals/${proposalId}/compliance-matrix/shred/preview`, {
      raw_text: rawText,
      use_ai: useAi,
      replace_existing: true,
    }),
  shredApply: (proposalId: string, rawText: string, useAi = false, replaceExisting = true) =>
    api.post(`/api/proposals/${proposalId}/compliance-matrix/shred/apply`, {
      raw_text: rawText,
      use_ai: useAi,
      replace_existing: replaceExisting,
    }),
  gapAnalysis: (proposalId: string) =>
    api.get(`/api/proposals/${proposalId}/compliance-matrix/gap-analysis`),
  updateItem: (proposalId: string, itemId: string, payload: {
    status?: string;
    evidence?: string;
    section_mapped?: string;
    is_mandatory?: boolean;
  }) =>
    api.patch(`/api/proposals/${proposalId}/compliance-matrix/${itemId}`, payload),
  // Counts + completeness + disqualified/failed_mandatory (mandatory pass/fail gates).
  summary: (proposalId: string) =>
    api.get(`/api/proposals/${proposalId}/compliance-matrix-summary`),
};

// ── Bid-info coverage: firm-profile data + bid strategy ────

export const referees = {
  list: (params?: Record<string, unknown>) => api.get('/api/referees', { params }),
  create: (data: Record<string, unknown>) => api.post('/api/referees', data),
  update: (id: string, data: Record<string, unknown>) => api.patch(`/api/referees/${id}`, data),
  delete: (id: string) => api.delete(`/api/referees/${id}`),
};

export const safetyStatistics = {
  list: () => api.get('/api/safety-statistics'),
  create: (data: Record<string, unknown>) => api.post('/api/safety-statistics', data),
  update: (id: string, data: Record<string, unknown>) => api.patch(`/api/safety-statistics/${id}`, data),
  delete: (id: string) => api.delete(`/api/safety-statistics/${id}`),
};

export const insurancePolicies = {
  list: () => api.get('/api/insurance-policies'),
  create: (data: Record<string, unknown>) => api.post('/api/insurance-policies', data),
  update: (id: string, data: Record<string, unknown>) => api.patch(`/api/insurance-policies/${id}`, data),
  delete: (id: string) => api.delete(`/api/insurance-policies/${id}`),
};

export const bidStrategy = {
  get: (proposalId: string) => api.get(`/api/proposals/${proposalId}/bid-strategy`),
  update: (proposalId: string, data: { win_themes?: string[]; client_hot_buttons?: string[] }) =>
    api.put(`/api/proposals/${proposalId}/bid-strategy`, data),
};

// ── Content Library (Phase 2) ──────────────────────────────

export const contentLibrary = {
  list: (params?: Record<string, unknown>) => api.get('/api/content-library', { params }),
  get: (id: string) => api.get(`/api/content-library/${id}`),
  create: (data: Record<string, unknown>) => api.post('/api/content-library', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/api/content-library/${id}`, data),
  delete: (id: string) => api.delete(`/api/content-library/${id}`),
  search: (data: Record<string, unknown>) => api.post('/api/content-library/search', data),
  harvest: (proposalId: string) => api.post(`/api/content-library/harvest/${proposalId}`),
  suggestions: (params: Record<string, unknown>) => api.get('/api/content-library/suggestions/for-section', { params }),
  recordUsage: (id: string) => api.post(`/api/content-library/${id}/use`),
};

// ── CV Profiles (Phase 2) ──────────────────────────────────

export const cv = {
  get: (personId: string, variant: string) => api.get(`/api/people/${personId}/cv-profile`, { params: { variant } }),
  generate: (personId: string, data: Record<string, unknown>) => api.post(`/api/people/${personId}/cv-profile/generate`, data),
  update: (personId: string, cvId: string, data: Record<string, unknown>) => api.put(`/api/people/${personId}/cv-profile/${cvId}`, data),
  export: (personId: string) => api.post(`/api/people/${personId}/cv-profile/export`, null, { responseType: 'blob' }),
};

// ── Comments (Phase 3) ─────────────────────────────────────

export const comments = {
  list: (proposalId: string, params?: Record<string, unknown>) => api.get(`/api/proposals/${proposalId}/comments`, { params }),
  create: (proposalId: string, data: Record<string, unknown>) => api.post(`/api/proposals/${proposalId}/comments`, data),
  resolve: (proposalId: string, commentId: string) => api.patch(`/api/proposals/${proposalId}/comments/${commentId}/resolve`),
  delete: (proposalId: string, commentId: string) => api.delete(`/api/proposals/${proposalId}/comments/${commentId}`),
};

// ── Versions (Phase 3) ─────────────────────────────────────

export const versions = {
  list: (proposalId: string) => api.get(`/api/proposals/${proposalId}/versions`),
  get: (proposalId: string, versionId: string) => api.get(`/api/proposals/${proposalId}/versions/${versionId}`),
  create: (proposalId: string, data: Record<string, unknown>) => api.post(`/api/proposals/${proposalId}/versions`, data),
  restore: (proposalId: string, versionId: string) => api.post(`/api/proposals/${proposalId}/versions/${versionId}/restore`),
  diff: (proposalId: string, v1: string, v2: string) => api.get(`/api/proposals/${proposalId}/versions/${v1}/diff/${v2}`),
};

// ── Approvals (Phase 3) ────────────────────────────────────

export const approvals = {
  get: (proposalId: string) => api.get(`/api/proposals/${proposalId}/approvals`),
  init: (proposalId: string) => api.post(`/api/proposals/${proposalId}/approvals/init`),
  decide: (proposalId: string, data: Record<string, unknown>) => api.post(`/api/proposals/${proposalId}/approvals/decide`, data),
};

// ── Notifications (Phase 3) ────────────────────────────────

export const notifications = {
  list: (params?: Record<string, unknown>) => api.get('/api/notifications', { params }),
  unreadCount: () => api.get('/api/notifications/unread-count'),
  markRead: (id: string) => api.patch(`/api/notifications/${id}/read`),
  markAllRead: () => api.post('/api/notifications/mark-all-read'),
};

// ── Tenders (Phase 4) ──────────────────────────────────────

export const tenders = {
  list: (params?: Record<string, unknown>) => api.get('/api/tenders', { params }),
  get: (id: string) => api.get(`/api/tenders/${id}`),
  create: (data: Record<string, unknown>) => api.post('/api/tenders', data),
  updateStatus: (id: string, data: Record<string, unknown>) => api.patch(`/api/tenders/${id}/status`, data),
  delete: (id: string) => api.delete(`/api/tenders/${id}`),
  scan: () => api.post('/api/tenders/scan'),
  stats: () => api.get('/api/tenders/stats/summary'),
  createProposal: (id: string) => api.post(`/api/tenders/${id}/create-proposal`),
  // F185 (V120+ T3): paste-based import for Tenderlink alert emails /
  // web listings since the platform has no public API. Preview returns
  // the parsed fields for user review; import persists.
  previewTenderlink: (raw_text: string) =>
    api.post('/api/tenders/import/tenderlink/preview', { raw_text }),
  importTenderlink: (raw_text: string) =>
    api.post('/api/tenders/import/tenderlink', { raw_text }),
  // F188 (V120+ T5): upload a PDF/DOCX attachment for a tender. Text
  // gets extracted server-side and appended to tender.attached_text so
  // the existing search filter picks it up.
  attachPdf: (tenderId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/api/tenders/${tenderId}/attach`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// ── Tender Configuration ─────────────────────────────────────

export const tenderConfig = {
  sources: () => api.get('/api/config/tender-sources'),
  getSettings: () => api.get('/api/config/tender-settings'),
  updateSettings: (data: Record<string, unknown>) => api.put('/api/config/tender-settings', data),
};

// ── Integrations (Phase 4) ─────────────────────────────────

export const integrations = {
  list: () => api.get('/api/integrations'),
  get: (id: string) => api.get(`/api/integrations/${id}`),
  create: (data: Record<string, unknown>) => api.post('/api/integrations', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/api/integrations/${id}`, data),
  delete: (id: string) => api.delete(`/api/integrations/${id}`),
  test: (id: string) => api.post(`/api/integrations/${id}/test`),
  sync: (id: string) => api.post(`/api/integrations/${id}/sync`),
  // OAuth-redirect connectors (sharepoint, google_drive, salesforce, hubspot,
  // confluence). Returns {auth_url}; the page redirects the browser there.
  // 503s with a `detail` message when the operator hasn't set the OAuth app
  // secrets — surface that detail as a toast.
  connect: (provider: string) => api.get(`/api/integrations/${provider}/connect`),
};

// ── Competitors (Phase 6) ──────────────────────────────────

export const competitors = {
  list: (params?: Record<string, unknown>) => api.get('/api/competitors', { params }),
  get: (id: string) => api.get(`/api/competitors/${id}`),
  create: (data: Record<string, unknown>) => api.post('/api/competitors', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/api/competitors/${id}`, data),
  delete: (id: string) => api.delete(`/api/competitors/${id}`),
  insights: (id: string) => api.get(`/api/competitors/${id}/insights`),
  marketOverview: () => api.get('/api/competitors/market-overview'),
};

// ── Bid Results (F15) ──────────────────────────────────────

export const bidResults = {
  list: (params?: Record<string, unknown>) => api.get('/api/bid-results', { params }),
  get: (id: string) => api.get(`/api/bid-results/${id}`),
  create: (data: Record<string, unknown>) => api.post('/api/bid-results', data),
  delete: (id: string) => api.delete(`/api/bid-results/${id}`),
};

// ── Certifications (Phase 6) ───────────────────────────────

export const certifications = {
  list: (params?: Record<string, unknown>) => api.get('/api/certifications', { params }),
  get: (id: string) => api.get(`/api/certifications/${id}`),
  create: (data: Record<string, unknown>) => api.post('/api/certifications', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/api/certifications/${id}`, data),
  delete: (id: string) => api.delete(`/api/certifications/${id}`),
  expiring: (days: number) => api.get('/api/certifications/alerts/expiring', { params: { days } }),
  complianceCheck: (proposalId: string) => api.get(`/api/certifications/compliance-check/${proposalId}`),
};

// ── Lessons Learned (Phase 6) ──────────────────────────────

export const lessons = {
  list: (params?: Record<string, unknown>) => api.get('/api/lessons', { params }),
  forProposal: (proposalId: string) => api.get(`/api/lessons/for-proposal/${proposalId}`),
  create: (data: Record<string, unknown>) => api.post('/api/lessons', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/api/lessons/${id}`, data),
  delete: (id: string) => api.delete(`/api/lessons/${id}`),
};

// ── Advanced Analytics (Phase 6) ───────────────────────────

export const advancedAnalytics = {
  winLoss: () => api.get('/api/analytics/advanced/win-loss-analysis'),
  pipelineForecast: () => api.get('/api/analytics/advanced/pipeline-forecast'),
  bidVelocity: () => api.get('/api/analytics/advanced/bid-velocity'),
  teamUtilization: () => api.get('/api/analytics/advanced/team-utilization'),
  resourceConflicts: () => api.get('/api/analytics/advanced/resource-conflicts'),
  clientScores: () => api.get('/api/analytics/advanced/client-scores'),
  feeBenchmarks: () => api.get('/api/analytics/advanced/fee-benchmarks'),
  capacityPlan: () => api.get('/api/analytics/advanced/capacity-plan'),
};

// ── API Keys (Phase 7) ─────────────────────────────────────

export const apiKeys = {
  list: () => api.get('/api/api-keys'),
  create: (data: Record<string, unknown>) => api.post('/api/api-keys', data),
  delete: (id: string) => api.delete(`/api/api-keys/${id}`),
  rotate: (id: string) => api.post(`/api/api-keys/${id}/rotate`),
  availableScopes: () => api.get('/api/api-keys/scopes'),
};

// ── Webhooks (Phase 7) ─────────────────────────────────────

export const webhooks = {
  list: () => api.get('/api/webhooks'),
  create: (data: Record<string, unknown>) => api.post('/api/webhooks', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/api/webhooks/${id}`, data),
  delete: (id: string) => api.delete(`/api/webhooks/${id}`),
  test: (id: string) => api.post(`/api/webhooks/${id}/test`),
  availableEvents: () => api.get('/api/webhooks/events'),
};

// ── CRM (Phase 7) ──────────────────────────────────────────

export const crm = {
  contacts: () => api.get('/api/crm/contacts'),
  sync: () => api.post('/api/crm/sync'),
  createDeal: (proposalId: string) => api.post(`/api/crm/create-deal/${proposalId}`),
};

// ── E-Signature (Phase 7) ──────────────────────────────────

export const esign = {
  send: (proposalId: string, data: Record<string, unknown>) => api.post(`/api/esign/send/${proposalId}`, data),
  status: (proposalId: string) => api.get(`/api/esign/status/${proposalId}`),
};

// ── Post-Win (Phase 7) ─────────────────────────────────────

export const postWin = {
  handoff: (proposalId: string) => api.get(`/api/proposals/${proposalId}/handoff`),
  convertToProject: (proposalId: string) => api.post(`/api/proposals/${proposalId}/convert-to-project`),
};

// ── Branding (Phase 7) ─────────────────────────────────────

export const branding = {
  get: () => api.get('/api/branding'),
  update: (data: Record<string, unknown>) => api.put('/api/branding', data),
};

// ── Brand Voice (F3) ──────────────────────────────────────

export const brandVoice = {
  train: (samples: string[]) => api.post('/api/branding/brand-voice/train', { samples }),
  get: () => api.get('/api/branding/brand-voice'),
  clear: () => api.delete('/api/branding/brand-voice'),
};

// ── SSO (Phase 7) ──────────────────────────────────────────

export const sso = {
  samlLogin: (orgSlug: string) => api.get(`/api/sso/saml/login/${orgSlug}`),
  oidcLogin: (orgSlug: string) => api.get(`/api/sso/oidc/login/${orgSlug}`),
};

// ── Engineering Standards Library (F12) ─────────────────────

export const standards = {
  suggest: (params: { disciplines: string; location?: string; project_type?: string }) =>
    api.get('/api/standards/suggest', { params }),
  search: (q: string) => api.get('/api/standards/search', { params: { q } }),
  all: () => api.get('/api/standards/all'),
};

// ── Excel Export ───────────────────────────────────────────

export const excelExport = {
  fee: (proposalId: string) => api.get(`/api/export/fee/${proposalId}`, { responseType: 'blob' }),
  analytics: () => api.get('/api/export/analytics', { responseType: 'blob' }),
  proposals: () => api.get('/api/export/proposals', { responseType: 'blob' }),
};

// ── (Cloud Storage + Calendar Sync clients removed 2026-05-22 — F193
//     orphan-cleanup: 0 component references across the frontend per
//     verified grep. Backend routes/cloud_storage.py + routes/calendar_sync.py
//     remain — re-wire here when the feature ships. The F184 audit
//     also flagged integrations/crm/advancedAnalytics as orphan but
//     all three are wired in active components — kept as-is.)

// ── Client Intelligence ──────────────────────────────────────

export const clients = {
  list: () => api.get('/api/clients'),
  predictions: () => api.get('/api/clients/predictions'),
  profile: (clientName: string) => api.get(`/api/clients/${encodeURIComponent(clientName)}/profile`),
  // F186 (V120+ T3b): batch dossier presence — for a list of client
  // names, return {name: {project_count, proposal_count, win_rate,
  // last_engaged}} for those WITH history. One round trip for the
  // entire tender list instead of N per-card profile calls.
  dossierPresence: (clientNames: string[]) =>
    api.post('/api/clients/dossier-presence', { client_names: clientNames }),
  update: (clientId: string, data: Record<string, unknown>) => api.patch(`/api/clients/${clientId}`, data),
};

// ── Intelligence ──────────────────────────────────────────────

export const intelligence = {
  overview: () => api.get('/api/intelligence/overview'),
  winRate: (groupBy: string) => api.get('/api/intelligence/win-rate', { params: { group_by: groupBy } }),
  // F203 — win rate grouped by originating tender source. Manual
  // entries bucket under 'manual'. Sorted by (win_rate, total) desc
  // server-side so 1-of-1 small-N sources don't crown the leaderboard.
  winRateBySource: () => api.get('/api/intelligence/win-rate-by-source'),
  // F303 — engagement aggregates from proposal_views (published web
  // proposals). Open rate, total views, unique viewers, avg time-on-
  // page, top-engaged proposals + sections.
  engagement: () => api.get('/api/intelligence/engagement'),
  // F350 — weighted pipeline forecast (draft 0.1, in_progress 0.3,
  // review/submitted 0.5; won/lost/cancelled excluded as terminal).
  revenueForecast: () => api.get('/api/intelligence/revenue-forecast'),
  // T4 (F412) — avg estimate-vs-actual hours variance per project
  // type, won proposals only.
  estimateAccuracy: () => api.get('/api/intelligence/estimate-accuracy'),
  // T9 — margin / utilization (Phase D)
  marginByProjectType: () => api.get('/api/intelligence/margin-by-project-type'),
  teamUtilization: (windowDays: number = 30) =>
    api.get('/api/intelligence/utilization', { params: { window_days: windowDays } }),
  teamCombinations: () => api.get('/api/intelligence/team-combinations'),
  feeBenchmarks: (projectType?: string) => api.get('/api/intelligence/fee-benchmarks', { params: { project_type: projectType } }),
  teamRecommendations: (projectType?: string, client?: string) =>
    api.get('/api/intelligence/team-recommendations', { params: { project_type: projectType, client } }),
  clientProfile: (clientName: string) => api.get(`/api/intelligence/client/${encodeURIComponent(clientName)}`),
  impactReport: (days: number = 90) => api.get('/api/intelligence/impact-report', { params: { days } }),
};

// ── Billing ──────────────────────────────────────────────────

export const billing = {
  status: () => api.get('/api/billing/status'),
  usage: () => api.get('/api/billing/usage'),
  config: () => api.get('/api/billing/config'),
  createCheckout: (tier: string) => api.post('/api/billing/checkout', { tier }),
  createPortal: () => api.post('/api/billing/portal'),
  invoices: () => api.get('/api/billing/invoices'),
};

// ── Submission Tracking ──────────────────────────────────────

export const submissionTracking = {
  list: (proposalId: string) => api.get(`/api/proposals/${proposalId}/submission-events`),
  create: (proposalId: string, data: Record<string, unknown>) =>
    api.post(`/api/proposals/${proposalId}/submission-events`, data),
  delete: (proposalId: string, eventId: string) =>
    api.delete(`/api/proposals/${proposalId}/submission-events/${eventId}`),
  recordResult: (proposalId: string, data: Record<string, unknown>) =>
    api.post(`/api/proposals/${proposalId}/submission-events/record-result`, data),
};

// ── Workflow Templates ──────────────────────────────────────

export const workflowTemplates = {
  list: () => api.get('/api/workflow-templates'),
  create: (data: Record<string, unknown>) => api.post('/api/workflow-templates', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/api/workflow-templates/${id}`, data),
  delete: (id: string) => api.delete(`/api/workflow-templates/${id}`),
};

// ── Audit Log ────────────────────────────────────────────────

export const auditLog = {
  list: (params?: Record<string, unknown>) => api.get('/api/audit-log', { params }),
  summary: () => api.get('/api/audit-log/summary'),
  exportCsv: (params?: Record<string, unknown>) =>
    api.get('/api/audit-log/export.csv', { params, responseType: 'blob' }),
};

// ── Offices ──────────────────────────────────────────────────

export const offices = {
  list: () => api.get('/api/offices'),
  create: (data: Record<string, unknown>) => api.post('/api/offices', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/api/offices/${id}`, data),
  delete: (id: string) => api.delete(`/api/offices/${id}`),
};

// ── Custom DOCX Templates ────────────────────────────────────

export const customTemplates = {
  mergeFields: () => api.get('/api/custom-templates/merge-fields'),
  list: () => api.get('/api/custom-templates'),
  upload: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/custom-templates', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (filename: string) => api.delete(`/api/custom-templates/${filename}`),
};

// ── Admin (User Management) ─────────────────────────────────

export const admin = {
  listUsers: () => api.get('/api/admin/users'),
  inviteUser: (data: { email: string; name: string; role: string; password: string }) =>
    api.post('/api/admin/invite', data),
  pendingInvites: () => api.get('/api/admin/invites/pending'),
  resendInvite: (email: string) => api.post(`/api/admin/invites/${encodeURIComponent(email)}/resend`),
  updateRole: (userId: string, role: string) =>
    api.patch(`/api/admin/users/${userId}/role`, { role }),
  deactivateUser: (userId: string) =>
    api.delete(`/api/admin/users/${userId}`),
  reactivateUser: (userId: string) =>
    api.patch(`/api/admin/users/${userId}/reactivate`),
  exportData: () => api.get('/api/admin/export'),
  modelEvaluation: () => api.get('/api/admin/model-evaluation'),
  tenderSourceHealth: () => api.get('/api/admin/health/tenders'),
  // F164 (Phase 26 data pipeline, 2026-05-19): DLQ + data-quality
  // operator endpoints. dlqList accepts optional filter params; the
  // widget calls dataQualitySnapshot which returns both DLQ aggregate
  // + per-source volume bands in one round-trip.
  dlqList: (params?: { source?: string; error_kind?: string; only_open?: boolean; limit?: number }) =>
    api.get('/api/admin/health/dlq', { params }),
  dlqResolve: (eventId: string, notes?: string) =>
    api.post(`/api/admin/dlq/${eventId}/resolve`, null, { params: notes ? { notes } : undefined }),
  // F166 (Phase 26 follow-up, 2026-05-19): batch-resolve to handle the
  // common operator pattern "fix one upstream bug → resolve dozens
  // of entries with the same root cause" without clicking N times.
  dlqBatchResolve: (payload: {
    event_ids?: string[];
    source?: string;
    error_kind?: string;
    notes?: string;
    all_orgs?: boolean;
  }) => api.post('/api/admin/dlq/batch-resolve', payload),
  dlqMarkRetried: (eventId: string) =>
    api.post(`/api/admin/dlq/${eventId}/retry-mark`),
  dataQualitySnapshot: () => api.get('/api/admin/health/data-quality'),
  // F171 + F172: alert webhook config (GET/PUT/test) for the Settings
  // → Alerts panel. PUT with null clears.
  alertWebhookGet: () => api.get('/api/admin/alert-webhook'),
  alertWebhookSet: (webhook_url: string | null) =>
    api.put('/api/admin/alert-webhook', { webhook_url }),
  alertWebhookTest: () => api.post('/api/admin/alert-webhook/test'),
};

// ── Weekly Digest (F187 + F201 — V120+ retention T4) ───────────

export const digests = {
  preview: () => api.get('/api/digests/preview'),  // returns HTML
  sendNow: () => api.post('/api/digests/send-now'),
  // F201 — per-user subscription state for the cron-driven Monday email.
  // GET returns {subscribed, last_sent_at}; PUT toggles + returns same shape.
  getSubscription: () => api.get('/api/digests/subscription'),
  putSubscription: (subscribed: boolean) =>
    api.put('/api/digests/subscription', { subscribed }),
};

// ── Wizard Sessions (F198 — cross-device resume) ────────────────
// Per-user-per-wizard saved state. GET returns 404 when no session
// exists (frontend treats as "fall back to localStorage"). PUT
// upserts; DELETE clears once the wizard completes.

export const wizardSessions = {
  get: (kind: string) => api.get(`/api/wizard-sessions/${kind}`),
  put: (kind: string, state: unknown) =>
    api.put(`/api/wizard-sessions/${kind}`, { state }),
  delete: (kind: string) => api.delete(`/api/wizard-sessions/${kind}`),
};

// ── RFP Documents (F184 — recently parsed surface) ─────────────

export const rfpDocuments = {
  listUnlinked: (limit?: number) =>
    api.get('/api/rfp-documents/unlinked', { params: { limit: limit ?? 10 } }),
};

// ── Compliance Matrix summaries (F184 — Dashboard widget) ──────

export const complianceMatrices = {
  summary: (proposalId: string) =>
    api.get(`/api/proposals/${proposalId}/compliance-matrix-summary`),
  listInFlight: (limit?: number) =>
    api.get('/api/compliance-matrices/summary', { params: { limit: limit ?? 5 } }),
};

// ── Decision Log (F184 — retrieval layer for F182 capture) ──────

export const decisions = {
  list: (params?: {
    decision_type?: string;
    resource_id?: string;
    user_email?: string;  // F208
    mine?: boolean;       // F208
    start_date?: string;
    end_date?: string;
    limit?: number;
    offset?: number;
  }) => api.get('/api/decisions', { params }),
  summary: () => api.get('/api/decisions/summary'),
  // F200 — fetch most-recent N win + N loss rationales (only those
  // where the user typed something into the win/loss prompt).
  outcomeRationales: (limit: number = 10) =>
    api.get('/api/decisions/outcome-rationales', { params: { limit } }),
  // F202 — fetch relevant past-win rationales for a given client_name,
  // for use as reference content during proposal drafting. Optional
  // client_name filter; without it returns "recent wins across all
  // clients" as fallback.
  relevantWins: (params?: { client_name?: string; limit?: number }) =>
    api.get('/api/decisions/relevant-wins', { params }),
};

// (F184: `notifications` export already exists at L528 from Phase 3 —
// F184 adds new writers — decision_log, parse_rfp, parse_rfp_batch —
// so the bell that was previously empty now has real signal. No new
// client wrapper needed.)

// In-app feedback widget — posts to audit log + (post-pilot) outbound email.
export const feedback = {
  submit: (data: { category: 'question' | 'bug' | 'idea'; message: string; url?: string; user_agent?: string }) =>
    api.post('/api/feedback', data),
};

// Te Tiriti / mana whenua boilerplate library — backend at routes/tiriti.py
// returns text fragments and a region→iwi lookup, both with a
// `warning` field that the UI MUST render prominently. The fragments are
// starting-point text only — director cultural-safety review is required
// before they ship in any client-facing document.
export const tiriti = {
  fragments: () => api.get('/api/tiriti/fragments'),
  iwiByRegion: (region: string) =>
    api.get('/api/tiriti/iwi-by-region', { params: { region } }),
};

// Section pulse — per-section confidence (length, placeholder leaks,
// coverage, recency). Cheap deterministic; safe to call on every save.
export const sectionPulse = {
  get: (proposalId: string) =>
    api.get(`/api/proposals/${proposalId}/section-pulse`),
};

// Acronym & terminology consistency checker.
export const acronymCheck = {
  run: (proposalId: string, strict = false) =>
    api.get(`/api/proposals/${proposalId}/acronym-check`, { params: { strict } }),
};

// Capability Statement — 1-page firm overview. Preview returns the data;
// download returns the DOCX. The audience param affects which projects
// get prioritised in the flagship-projects slot.
export const capabilityStatement = {
  preview: (audience: 'general' | 'nz_govt' | 'council' | 'pacific' = 'general', useAi = true) =>
    api.get('/api/capability-statement/preview', { params: { audience, use_ai: useAi } }),
  download: (audience: 'general' | 'nz_govt' | 'council' | 'pacific' = 'general', useAi = true) =>
    api.get('/api/capability-statement/download', {
      params: { audience, use_ai: useAi },
      responseType: 'blob',
    }),
};

// Derived team bios — computed on read so CPD/cert updates reflect
// immediately. Style: 'tender' (proposal section bio), 'cv' (full block),
// or 'capstmt' (one-line capability-statement entry).
export const derivedBio = {
  get: (personId: string, style: 'tender' | 'cv' | 'capstmt' = 'tender') =>
    api.get(`/api/people/${personId}/derived-bio`, { params: { style } }),
};

export default api;
